<template>
  <div
    :id="idName"
    @click="typeDialog = !typeDialog">
    <slot>
      Download {{ name }}
    </slot>
    <v-dialog
      v-model="typeDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="40%"
    >
      <v-card>
        <v-col
          class="d-flex pb-0"
        >
          <v-btn
            icon
            color="grey darken-2"
            @click="typeDialog = false"
          >
            <v-icon
              class="close-hover"
              large
            >
              mdi-close
            </v-icon>
          </v-btn>
          <h2 class="mx-auto font-weight-medium mt-2">
            {{ $t('excelXport') }}
            <v-icon
              style="font-size: 26px"
              color="teal"
            >
              mdi-file-excel-outline
            </v-icon>
          </h2>
        </v-col>
        <v-row class="ma-0">
          <v-col class="d-flex pb-2 mx-2">
            <div class="mt-5">
              {{ $t('selectHeaderLanguage') }}
            </div>
            <v-radio-group
              v-model="lang"
              row
              hide-details
            >
              <v-radio
                :label="$t('systemLanguage')"
                value="main"
              ></v-radio>
              <v-radio
                :label="$t('english')"
                value="eng"
              ></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row class="ma-0">
          <v-col class="d-flex pt-0 pb-5 mx-2">
            <div class="mt-0  pink--text">
              {{ $t('excelExportComment') }}
            </div>
          </v-col>
        </v-row>
        <v-card-actions>
          <v-btn
            v-if="!excelIndex"
            color="primary"
            @click="getAll"
          >
            {{ $t('getAllRows') }}
          </v-btn>
          <v-spacer/>
          <v-btn
            color="teal"
            @click="generate"
          >
            {{ $t('getPageRows') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="progressDialog"
      hide-overlay
      persistent
      width="400"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text v-if="totalProp && pageProp">
          <div align="center">
            <p class="white--text mb-0">{{$t('gettingData...pleaseWaite')}}</p>
          </div>
          <v-progress-linear
            v-model="progressValue"
            color="amber"
            height="5"
          >
          </v-progress-linear>
          <div align="center">
            <p class="white--text mb-0">{{ Math.ceil(progressValue) }}%</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import download from 'browser-downloads'

export default {
  name: 'export-excel',
  props: {
    excelIndex: {
      type: Boolean,
      default: false
    },
    totalProp: {
      type: Number,
      default: 1
    },
    pageProp: {
      type: Number,
      default: 1
    },
    // mime type [xls, csv]
    type: {
      type: String,
      default: 'xls'
    },
    // Json to download
    data: {
      type: Array,
      required: false,
      default: null
    },
    // fields inside the Json Object that you want to export
    // if no given, all the properties in the Json are exported
    fields: {
      type: Object,
      required: false
    },
    // this prop is used to fix the problem with other components that use the
    // variable fields, like vee-validate. exportFields works exactly like fields
    exportFields: {
      type: Object,
      required: false
    },
    // Use as fallback when the row has no field values
    defaultValue: {
      type: String,
      required: false,
      default: ''
    },
    // Title(s) for the data, could be a string or an array of strings (multiple titles)
    title: {
      default: null
    },
    // Footer(s) for the data, could be a string or an array of strings (multiple footers)
    footer: {
      default: null
    },
    // filename to export
    name: {
      type: String,
      default: 'data.xls'
    },
    fetch: {
      type: Function
    },
    meta: {
      type: Array,
      default: () => []
    },
    worksheet: {
      type: String,
      default: 'Sheet1'
    },
    // event before generate was called
    beforeGenerate: {
      type: Function
    },
    // event before download pops up
    beforeFinish: {
      type: Function
    }
  },
  data: () => ({
    typeDialog: false,
    progressDialog: false,
    lang: 'main'
  }),
  computed: {
    progressValue () {
      return (this.pageProp / this.totalProp) * 100
    },
    idName () {
      var now = new Date().getTime()
      return 'export_' + now
    },

    downloadFields () {
      if (this.fields !== undefined) return this.fields

      if (this.exportFields !== undefined) return this.exportFields
    }
  },
  mounted () {
    var barcode = document.createElement('script')
    // barcode.setAttribute('src', 'https://cdn.jsdelivr.net/gh/linways/table-to-excel@v1.0.4/dist/tableToExcel.js')
    barcode.setAttribute('src', 'https://abr.abriron.com/js/excel.js')
    document.head.appendChild(barcode)
  },
  methods: {
    getAll () {
      this.typeDialog = false
      this.progressDialog = true
      this.$emit('getAllRows', true)
    },
    async generate () {
      this.typeDialog = false
      this.progressDialog = false
      if (typeof this.beforeGenerate === 'function') {
        await this.beforeGenerate()
      }
      let data = this.data
      if (typeof this.fetch === 'function' || !data) {
        data = await this.fetch()
      }

      if (!data || !data.length) {
        return
      }

      const json = this.getProcessedJson(data, this.downloadFields)
      if (this.type === 'html') {
        // mainly for testing
        return this.export(
          this.jsonToXLS(json),
          this.name.replace('.xls', '.html'),
          'text/html'
        )
      } else if (this.type === 'csv') {
        return this.export(
          this.jsonToCSV(json),
          this.name.replace('.xls', '.csv'),
          'application/csv'
        )
      }
      return this.export(
        this.jsonToXLS(json),
        this.name,
        'application/vnd.ms-excel'
      )
    },
    /*
        Use downloadjs to generate the download link
        */
    export: async function (data, filename, mime) {
      const blob = this.base64ToBlob(data, mime)
      const parser = new DOMParser()
      const html = parser.parseFromString(data, 'text/html')
      var table = html.getElementsByTagName('table')

      // var toExcel = new window.TableToExcel
      window.TableToExcel.convert(table[0], { // html code may contain multiple tables so here we are refering to 1st table tag
        name: 'export.xlsx', // fileName you could use any name
        sheet: {
          name: 'Sheet 1' // sheetName,
        },
        sheetViews: {
          rightToLeft: '1'
        }
      })
      // if (typeof this.beforeFinish === 'function')
      //     await this.beforeFinish();
      // download(blob, filename, mime);
    },
    /*
        jsonToXLS
        ---------------
        Transform json data into an xml document with MS Excel format, sadly
        it shows a prompt when it opens, that is a default behavior for
        Microsoft office and cannot be avoided. It's recommended to use CSV format instead.
        */
    jsonToXLS (data) {
      const colspan = Object.keys(data[0]).length
      var width = []
      for (let i = 0; i < colspan; i++) {
        width.push(15)
      }
      const xlsTemp =
        '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta name=ProgId content=Excel.Sheet> <meta name=Generator content="Microsoft Excel 11"><meta http-equiv="Content-Type" content="text/html; charset=UTF-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--><style>br {mso-data-placement: same-cell;}</style></head>' +
        '<body>' +
        '<table data-cols-width="' + width + '">${table}</table></body></html>'
      let xlsData = '<thead>'

      const _self = this

      // Header
      if (this.title != null && this.lang == 'main') {
        xlsData += this.parseExtraData(
          this.title,
          '<tr data-height="40"><th data-fill-color="CCC0DA" data-b-a-s="thin" data-a-h="center"' +
          '              data-a-v="middle" colspan="' + colspan + '">${data}</th></tr>'
        )
      }

      // Fields CCC0DA
      xlsData += '<tr>'
      for (const key in data[0]) {
        xlsData += '<th data-b-a-s="thin" data-a-rtl="true" data-f-bold="true" data-fill-color=\'e3e3e3\'>' + (this.lang == 'main' ? this.$t(key) : key) + '</th>'
      }
      xlsData += '</tr>'
      xlsData += '</thead>'

      // Data
      xlsData += '<tbody>'
      var app = this
      data.map(function (item, index) {
        xlsData += '<tr>'
        for (const key in item) {
          const forceStringFields = ['barcode']
          var datatype = 's'
          if (
            item[key] &&
            app.isNumeric(item[key]) &&
            item[key].length <= 20 &&
            !forceStringFields.includes(key)
          ) {
            datatype = 'n'
          }

          // if (item[key] && app.isNumeric(item[key]) && item[key].length <= 20) {
          //   datatype = 'n'
          // }
          xlsData += '<td data-a-rtl="true" data-t="' + datatype + '"  data-b-a-s="thin">' + _self.valueReformattedForMultilines(item[key]) + '</td>'
        }
        xlsData += '</tr>'
      })
      xlsData += '</tbody>'

      // Footer
      if (this.footer != null) {
        xlsData += '<tfoot>'
        xlsData += this.parseExtraData(
          this.footer,
          '<tr><td colspan="' + colspan + '">${data}</td></tr>'
        )
        xlsData += '</tfoot>'
      }

      return xlsTemp.replace('${table}', xlsData).replace('${worksheet}', this.worksheet)
    },

    isNumeric (string) {
      return Number.isFinite(+string)
    },
    /*
        jsonToCSV
        ---------------
        Transform json data into an CSV file.
        */
    jsonToCSV (data) {
      var csvData = []
      // Header
      if (this.title != null) {
        csvData.push(this.parseExtraData(this.title, '${data}\r\n'))
      }
      // Fields
      for (const key in data[0]) {
        csvData.push(key)
        csvData.push(',')
      }
      csvData.pop()
      csvData.push('\r\n')
      // Data
      data.map(function (item) {
        for (const key in item) {
          let escapedCSV = '=\"' + item[key] + '\"' // cast Numbers to string
          if (escapedCSV.match(/[,"\n]/)) {
            escapedCSV = '"' + escapedCSV.replace(/\"/g, '""') + '"'
          }
          csvData.push(escapedCSV)
          csvData.push(',')
        }
        csvData.pop()
        csvData.push('\r\n')
      })
      // Footer
      if (this.footer != null) {
        csvData.push(this.parseExtraData(this.footer, '${data}\r\n'))
      }
      return csvData.join('')
    },
    /*
        getProcessedJson
        ---------------
        Get only the data to export, if no fields are set return all the data
        */
    getProcessedJson (data, header) {
      const keys = this.getKeys(data, header)
      const newData = []
      const _self = this
      data.map(function (item, index) {
        const newItem = {}
        for (const label in keys) {
          const property = keys[label]
          newItem[label] = _self.getValue(property, item)
        }
        newData.push(newItem)
      })

      return newData
    },
    getKeys (data, header) {
      if (header) {
        return header
      }

      const keys = {}
      for (const key in data[0]) {
        keys[key] = key
      }
      return keys
    },
    /*
        parseExtraData
        ---------------
        Parse title and footer attribute to the csv format
        */
    parseExtraData (extraData, format) {
      let parseData = ''
      if (Array.isArray(extraData)) {
        for (var i = 0; i < extraData.length; i++) {
          parseData += format.replace('${data}', extraData[i])
        }
      } else {
        parseData += format.replace('${data}', extraData)
      }
      return parseData
    },

    getValue (key, item) {
      const field = typeof key !== 'object' ? key : key.field
      const indexes = typeof field !== 'string' ? [] : field.split('.')
      let value = this.defaultValue

      if (!field) {
        value = item
      } else if (indexes.length > 1) {
        value = this.getValueFromNestedItem(item, indexes)
      } else {
        value = this.parseValue(item[field])
      }

      if (key.hasOwnProperty('callback')) {
        value = this.getValueFromCallback(value, key.callback)
      }

      return value
    },

    /*
    convert values with newline \n characters into <br/>
    */
    valueReformattedForMultilines (value) {
      if (typeof (value) === 'string') {
        return (value.replace(/\n/ig, '<br/>'))
      } else {
        return (value)
      }
    },

    getValueFromNestedItem (item, indexes) {
      let nestedItem = item
      for (const index of indexes) {
        if (nestedItem) {
          nestedItem = nestedItem[index]
        }
      }
      return this.parseValue(nestedItem)
    },

    getValueFromCallback (item, callback) {
      if (typeof callback !== 'function') {
        return this.defaultValue
      }
      const value = callback(item)
      return this.parseValue(value)
    },
    parseValue (value) {
      return value || value === 0 || typeof value === 'boolean'
        ? value
        : this.defaultValue
    },
    base64ToBlob (data, mime) {
      const base64 = window.btoa(window.unescape(encodeURIComponent(data)))
      const bstr = atob(base64)
      let n = bstr.length
      const u8arr = new Uint8ClampedArray(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new Blob([u8arr], { type: mime })
    }
  } // end methods
}
</script>
